import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import midasInstance from '@rsos/midas/src/midasInstance';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import useDebounce from '@rsos/utils/useDebounce';

const TENTATIVE_UNAVAILABLE = 'Tentative ETA unavailable';
const TENTATIVE_FETCHING = 'Fetching Tentative ETA';

/**
 * Custom hook that fetches and manages tentative ETA (Estimated Time of Arrival) data for air ambulance services.
 *
 * @returns {Object|null} Returns an object with the following structure when data is available:
 * @returns {string} returns.display - The display text for the ETA
 * @returns {string|null} returns.providers - String of providers if available, null otherwise
 *
 * Possible return values:
 * - When data is available: { display: "ETA time", providers: "provider string" }
 * - When loading: { display: "Fetching Tentative ETA", providers: null }
 * - When error: { display: "Tentative ETA unavailable", providers: null }
 * - When no coordinates: null
 */
export const useTentativeETA = () => {
  const [etaData, setEtaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentPSAP = useSelector(state => state.psaps.currentPSAP);
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const existingRequest = useSelector(selectExistingRequest);
  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const tentativeETALocation = useSelector(
    state => state.airAmbulance.tentativeETALocation,
  );

  const isEditMode = !!existingRequest?.body;
  const tentativeCoordinates = useMemo(
    () => tentativeETALocation || existingRequest?.body?.location || null,
    [tentativeETALocation, existingRequest],
  );

  const hasLocation = useMemo(
    () => getMostRecentLoc(callQueue?.[selectedCallerID]),
    [callQueue, selectedCallerID],
  );

  const fetchETA = useCallback(
    async tentativeCoordinates => {
      if (!tentativeCoordinates) return;

      setLoading(true);
      setError(null);

      try {
        const callflow = '/air_ambulance/gmr_air_eta';
        const fromUnite = {
          logged_in_fname: currentPSAP?.contact_name || '',
          logged_in_lname: currentPSAP?.contact_name || '',
          logged_in_email: currentPSAP?.contact_email || '',
          ecc_id: currentPSAP?.account_id || '',
        };

        if (!isEditMode) {
          if (hasLocation) {
            fromUnite['caller_phone'] = `+${selectedCallerID}`;
          }
        }

        const payload = {
          callflow,
          blocking: true,
          variables: {
            from_unite: fromUnite,
            ext_trip_id: '',
            trip_data: {
              location: {
                latitude: tentativeCoordinates.latitude?.toString() || '',
                longitude: tentativeCoordinates.longitude?.toString() || '',
              },
            },
          },
        };
        if (existingRequest && existingRequest.body) {
          payload.variables.ext_trip_id = existingRequest?.body?.ext_trip_id;
        }

        const response = await midasInstance.post(
          '/internal_auth/trigger',
          payload,
          { headers: { retry: true } },
        );

        const parsed = JSON.parse(response.data.response_body);
        if (existingRequest && existingRequest.body) {
          parsed.extSystemTripId = existingRequest?.body?.ext_trip_id;
        }

        setEtaData(parsed);
      } catch (err) {
        console.error('Error fetching tentative ETA:', err); // eslint-disable-line
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [existingRequest, currentPSAP, hasLocation, isEditMode, selectedCallerID],
  );

  const debouncedFetchETA = useDebounce(fetchETA, 500, []);

  useEffect(() => {
    if (!loading) {
      setLoading(true);
    }
    debouncedFetchETA(tentativeCoordinates);
    // eslint-disable-next-line
  }, [tentativeCoordinates, debouncedFetchETA]);

  const tentativeETA = useMemo(() => {
    if (
      tentativeCoordinates &&
      !loading &&
      !error &&
      etaData &&
      etaData.extSystemTripId &&
      etaData?.tripLeg?.[0]?.eta?.display
    ) {
      return {
        display: etaData?.tripLeg?.[0]?.eta?.display,
        providers: etaData?.tripLeg?.[0]?.eta?.providers,
        extSystemTripId: etaData.extSystemTripId,
      };
    }
    if (!loading && error && tentativeCoordinates) {
      return {
        display: TENTATIVE_UNAVAILABLE,
        providers: null,
      };
    }
    if (tentativeCoordinates) {
      return {
        display: TENTATIVE_FETCHING,
        providers: null,
      };
    }
    return null;
  }, [etaData, error, loading, tentativeCoordinates]);

  return tentativeETA;
};

export default useTentativeETA;
